import {
  FETCH_CAMPAIGNS,
  DELETE_CAMPAIGN,
  CREATE_CAMPAIGN,
  FETCH_CAMPAIGN,
  UPDATE_CAMPAIGN,
  FETCH_PREVIEW,
  SEND_CAMPAIGN
} from "./types";
import * as campaignsApi from "@/api-client/campaigns";
import * as templateApi from "@/api-client/template";
import { convertDataFromApi } from "@/utils/util";
import { flatten, uniq, keyBy } from "lodash";
import { createNewTagsFromLocal } from "@/store/utils";
import { sortSearchPaginateActions } from "@/store/common/actions";
import * as lettersTemplateApi from "@/api-client/letters";

export default {
  // TODO: refactor this huge block
  async fetchCampaigns({ commit }, { page, search, sort, page_size } = {}) {
    commit({
      type: FETCH_CAMPAIGNS.request
    });

    try {
      const campaigns = await campaignsApi.fetchCampaignsList({
        page,
        search,
        sort,
        page_size
      });

      const { items, ...rest } = campaigns;

      const convertedData = items
        .filter(item => !item.deleted_at)
        .map(item => convertDataFromApi("campaign-letter", item));

      const templatesIdsPostCard = convertedData
        .filter(campaign => campaign.type === "postcard")
        .map(campaign => campaign.template_id);

      const templatesIdsLetter = convertedData
        .filter(campaign => campaign.type === "letter")
        .map(campaign => campaign.template_id);

      // adding comma manually even if only one element in array
      // to make this working for backend
      const uniqIds = uniq(flatten(templatesIdsPostCard));
      const templatesIdsStringPostcards =
        uniqIds.length === 1
          ? `${templatesIdsPostCard[0]},`
          : uniqIds.join(",");

      const uniqIdsL = uniq(flatten(templatesIdsLetter));
      const templatesIdsStringLetters =
        uniqIdsL.length === 1
          ? `${templatesIdsLetter[0]},`
          : uniqIdsL.join(",");

      const templatesFromApi = await templateApi.fetchTemplatesList({
        queryString: `&data-id=${templatesIdsStringPostcards.replace(
          ",,",
          ","
        )}`,
        page_size: 100
      });

      const lettersTemplatesFromApi = await lettersTemplateApi.fetchLettersTemplatesList(
        {
          queryString: `&data-id=${templatesIdsStringLetters.replace(
            ",,",
            ","
          )}`,
          page_size: 100
        }
      );

      const apiResp = await Promise.all([
        templatesFromApi,
        lettersTemplatesFromApi
      ]);

      const templates = keyBy([...apiResp[0].items, ...apiResp[1].items], "id");

      const dataWithTemplates = convertedData.map(campaign => {
        campaign.template = templates[campaign.template_id];
        return campaign;
      });

      commit({
        type: FETCH_CAMPAIGNS.success,
        payload: {
          items: dataWithTemplates,
          ...rest
        }
      });
    } catch (e) {
      commit({
        type: FETCH_CAMPAIGNS.error,
        error: e.message
      });
    }
  },

  async fetchCampaign({ commit, rootState }, { id, onSuccess, onError }) {
    commit({
      type: FETCH_CAMPAIGN.request
    });

    try {
      const campaign = await campaignsApi.fetchCampaignById(id);
      let convertedResp = convertDataFromApi("campaign-letter", campaign);
      const isLetter = convertedResp.type === "letter";
      const vuexTemplateKey = isLetter ? "lettersTemplates" : "templates";
      const { template_id } = convertedResp;

      let template = rootState[vuexTemplateKey].templatesList.filter(
        temp => temp.id === template_id
      )[0];

      if (!template) {
        if (!isLetter) {
          template = await templateApi.fetchTemplateById(template_id);
        } else {
          const lettersTemplatesFromApi = await lettersTemplateApi.fetchLettersTemplatesList(
            {
              queryString: `&data-id=${template_id},`
            }
          );

          template = await lettersTemplatesFromApi.items[0];
        }

        if (template.data) {
          convertedResp.template = { name: template.data.title };
        } else {
          convertedResp.template = template;
        }
      } else {
        convertedResp.template = template;
      }

      commit({
        type: FETCH_CAMPAIGN.success,
        payload: convertedResp
      });

      onSuccess ? onSuccess(convertedResp) : null;

      return campaign;
    } catch (e) {
      commit({
        type: FETCH_CAMPAIGN.error,
        payload: e.message
      });

      onError ? onError(e.message) : null;
    }
  },

  async deleteCampaign({ commit }, { id, callback }) {
    commit({
      type: DELETE_CAMPAIGN.request
    });

    try {
      await campaignsApi.deleteCampaign(id);

      commit({
        type: DELETE_CAMPAIGN.success,
        payload: id
      });

      callback ? callback(id) : null;
    } catch (e) {
      commit({
        type: DELETE_CAMPAIGN.error,
        payload: e.message
      });
    }
  },

  async createCampaign(
    { commit, rootState, dispatch },
    { data, onSuccess, onError }
  ) {
    commit({
      type: CREATE_CAMPAIGN.request
    });

    try {
      const tagsList = rootState.contacts.tags;
      data.tags = await createNewTagsFromLocal(dispatch, data.tags, tagsList);
      const resp = await campaignsApi.createCampaign(data);
      const { template_id } = data;

      let templateVuexStoreModule =
        data.type === "letter" ? "lettersTemplates" : "templates";

      const convertedResp = convertDataFromApi("campaign-letter", resp);

      let template = rootState[templateVuexStoreModule].templatesList.filter(
        temp => temp.id === template_id
      )[0];

      const mailer = convertedResp.mailers ? convertedResp.mailers[0] : {};

      if (!template && !data.deleted_at) {
        template = await templateApi.fetchTemplateById(template_id);

        if (template.data) {
          mailer.template = { name: template.data.title };
        } else {
          mailer.template = template;
        }
      } else {
        mailer.template = template;
      }

      commit({
        type: CREATE_CAMPAIGN.success,
        payload: convertedResp
      });

      onSuccess ? onSuccess(resp) : null;
    } catch (e) {
      commit({
        type: CREATE_CAMPAIGN.error,
        error: e.errors
      });

      onError ? onError(e) : null;
    }
  },

  async editCampaign(
    { commit, rootState, dispatch },
    { id, data, onSuccess, onError, simpleUpdate = false }
  ) {
    commit({
      type: UPDATE_CAMPAIGN.request
    });

    try {
      if (data.tags) {
        const tagsList = rootState.contacts.tags;
        data.tags = await createNewTagsFromLocal(dispatch, data.tags, tagsList);
      }

      const resp = await campaignsApi.editCampaign(id, data);
      const convertedResp = convertDataFromApi("campaign-letter", resp);
      const isLetter = data.type === "letter";

      let templateVuexStoreModule = isLetter ? "lettersTemplates" : "templates";

      if (!simpleUpdate) {
        const { template_id } = convertedResp;
        let template = rootState[templateVuexStoreModule].templatesList.filter(
          temp => temp.id === template_id
        )[0];

        const mailer = convertedResp.mailers ? convertedResp.mailers[0] : {};

        if (!template) {
          if (isLetter) {
            template = await lettersTemplateApi.fetchLettersTemplateById(
              template_id
            );
          } else {
            template = await templateApi.fetchTemplateById(template_id);
          }

          if (template.data) {
            mailer.template = { name: template.data.title };
          } else {
            mailer.template = template;
          }
        } else {
          mailer.template = template;
        }
      }

      commit({
        type: UPDATE_CAMPAIGN.success,
        payload: convertedResp
      });

      onSuccess ? onSuccess(resp) : null;
    } catch (e) {
      commit({
        type: UPDATE_CAMPAIGN.error,
        error: e.message
      });

      onError ? onError(e.message) : null;
    }
  },

  async fetchPreview({ commit }, id, { onSuccess, onError } = {}) {
    commit({
      type: FETCH_PREVIEW.request
    });

    try {
      const preview = await campaignsApi.fetchPreviewById(id);
      onSuccess && onSuccess(preview);

      commit({
        type: FETCH_PREVIEW.success,
        payload: { preview, id }
      });
    } catch (e) {
      let { message } = e;

      if (typeof message !== "string") {
        message = message.error;
      }

      commit({
        type: FETCH_PREVIEW.error,
        payload: message
      });

      onError ? onError(message) : null;
    }
  },

  async sendCampaign({ commit }, { id, onSuccess }) {
    commit({
      type: SEND_CAMPAIGN.request
    });

    try {
      await campaignsApi.sendCampaign(id);

      commit({
        type: SEND_CAMPAIGN.success
      });

      onSuccess ? onSuccess() : null;
    } catch (e) {
      commit({
        type: SEND_CAMPAIGN.error,
        payload: e.message
      });
    }
  },

  ...sortSearchPaginateActions
};
